import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { OSCE } from '@ils/osces/db/osce.js';
import { OSCEText } from '@ils/osces/db/osceText.js';
import { ExamSlotRatingDoc } from '@ils/ratings/db/rating.js';
import { templateOSCEText } from '@ils/slots/server/templateOSCEText.js';
import { Names } from '@ils/static/names.js';
import { DateTime } from 'luxon';
import { Answer } from '../../../src/db/answer.js';
import { isAudioLongEnoughResolver } from '../../../src/server/resolver/answer.js';

/**
 * @entity Slot
 */
@Entity()
export class Slot extends MongoEntity {
  static override _class = Names.Slot._class;
  static override _collectionName = Names.Slot._collectionName;

  static override get allFields() {
    return { ...selectionSet(() => Slot, 4), osce: OSCE.narrowAllFields };
  }
  static get allFieldsWithFullOSCE() {
    return selectionSet(() => Slot, 4);
  }

  constructor(doc: PartialEntityData<Slot>) {
    super(doc);
  }

  @Field(type => ID)
  tester?: string;

  @validate(nonOptional())
  @Field(type => ID)
  candidateID!: string;

  @Field(type => [ID], { default: v => v ?? [] })
  inspectors!: string[];

  @Field(type => DateTime)
  inspectionTime: Maybe<DateTime>;

  @Field(type => ID, { default: v => v ?? A.getGloballyUniqueID() })
  roomID!: string;

  @validate(nonOptional())
  @Field(type => DateTime)
  startTime!: DateTime;

  @Field(type => DateTime)
  endTime: Maybe<DateTime>;

  @Field(type => Boolean, { default: v => v ?? false })
  readonly isFallbackSlot!: boolean;

  @Field(type => String)
  fallbackSlotID?: string;

  @DataLoadedField(type => Answer, (slot: Slot) => ({ slotID: slot._id }))
  answer?: Answer;

  @DataLoadedField(type => [ExamSlotRatingDoc], (slot: Slot) => ({ slotID: slot._id, archived: { $ne: true } }))
  ratings!: ExamSlotRatingDoc[];

  @Field(type => DateTime)
  ratedAt: Maybe<DateTime>;

  @Field(type => String)
  osceID!: string;

  @DataLoadedField(type => OSCE, (slot: Slot) => slot.osceID)
  osce: Maybe<OSCE>;

  @Field(type => OSCEText, {
    resolve(this: Slot) {
      return () => templateOSCEText(this.osceID, this.tester);
    },
  })
  templatedOSCEtext: Maybe<OSCEText>;

  @Field(type => Boolean, { default: v => v ?? true })
  recordAudio!: boolean;

  @Field(type => String)
  operatorNotes?: Maybe<string>;

  @Field(type => Boolean, { default: v => v ?? false })
  isCandidateMissing!: boolean;

  @Field(type => Boolean, { default: v => v ?? false })
  problemWasReported!: boolean;

  /** only request this field if you actually need it, as it's making two requests */
  @Field(type => Boolean, {
    resolve: function (this: Slot) {
      return () => isAudioLongEnoughResolver(this._id, this.osceID);
    },
  })
  isAudioLongEnough?: boolean;
}
